import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  MessageSquare, 
  ThumbsUp, 
  MessageCircle, 
  Clock, 
  User, 
  Plus,
  Search,
  Filter,
  TrendingUp,
  BookOpen,
  ArrowLeft,
  Send,
  Heart,
  Eye,
  Pin,
  Award,
  Flame,
  ChevronRight,
  AlertCircle,
  Bell,
  BellOff,
  Flag,
  Lock,
  Shield
} from 'lucide-react';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const DiscussionForum = () => {
  const navigate = useNavigate();
  const { topicId } = useParams();
  
  const [discussions, setDiscussions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [showNewPostDialog, setShowNewPostDialog] = useState(false);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportTarget, setReportTarget] = useState({ type: '', id: '' });
  const [isBanned, setIsBanned] = useState(false);
  const [banInfo, setBanInfo] = useState(null);
  
  // New post form
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    subject: 'Physics',
    tags: ''
  });
  
  // Reply form
  const [replyContent, setReplyContent] = useState('');
  
  // User info
  const userId = localStorage.getItem('userId');
  const userName = localStorage.getItem('userName') || 'Anonymous';
  const userEmail = localStorage.getItem('userEmail') || '';
  const isLoggedIn = !!localStorage.getItem('userToken');

  useEffect(() => {
    fetchDiscussions();
    if (isLoggedIn && userId) {
      checkBanStatus();
    }
  }, [selectedSubject, sortBy]);

  useEffect(() => {
    if (topicId) {
      fetchTopicDetails(topicId);
      if (isLoggedIn && userId) {
        checkSubscriptionStatus(topicId);
      }
    } else {
      setSelectedTopic(null);
      setIsSubscribed(false);
    }
  }, [topicId]);

  const checkBanStatus = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/check-ban/${userId}`);
      const data = await res.json();
      if (data.is_banned) {
        setIsBanned(true);
        setBanInfo(data);
      }
    } catch (error) {
      console.error('Error checking ban status:', error);
    }
  };

  const checkSubscriptionStatus = async (topicId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/topic/${topicId}/subscription-status?user_id=${userId}`);
      const data = await res.json();
      setIsSubscribed(data.subscribed);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const handleSubscribe = async () => {
    if (!isLoggedIn) {
      toast({
        title: "Login Required",
        description: "Please login to subscribe to discussions",
        variant: "destructive"
      });
      return;
    }

    try {
      if (isSubscribed) {
        // Unsubscribe
        await fetch(`${BACKEND_URL}/api/forum/topic/${selectedTopic.id}/unsubscribe?user_id=${userId}`, {
          method: 'DELETE'
        });
        setIsSubscribed(false);
        toast({ title: "Unsubscribed", description: "You won't receive notifications for this topic" });
      } else {
        // Subscribe
        await fetch(`${BACKEND_URL}/api/forum/topic/${selectedTopic.id}/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: userId, user_email: userEmail })
        });
        setIsSubscribed(true);
        toast({ title: "Subscribed!", description: "You'll receive email notifications for new replies" });
      }
    } catch (error) {
      console.error('Error toggling subscription:', error);
      // Toggle locally for demo
      setIsSubscribed(!isSubscribed);
      toast({ title: isSubscribed ? "Unsubscribed" : "Subscribed!" });
    }
  };

  const handleReport = async () => {
    if (!reportReason.trim()) {
      toast({ title: "Please provide a reason", variant: "destructive" });
      return;
    }

    try {
      await fetch(`${BACKEND_URL}/api/forum/report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content_type: reportTarget.type,
          content_id: reportTarget.id,
          user_id: userId,
          reason: reportReason
        })
      });
      toast({ title: "Report Submitted", description: "Our moderators will review this content" });
    } catch (error) {
      toast({ title: "Report Submitted", description: "Our moderators will review this content" });
    }
    
    setShowReportDialog(false);
    setReportReason('');
    setReportTarget({ type: '', id: '' });
  };

  const openReportDialog = (type, id) => {
    setReportTarget({ type, id });
    setShowReportDialog(true);
  };

  const fetchDiscussions = async () => {
    try {
      const params = new URLSearchParams();
      if (selectedSubject !== 'all') params.append('subject', selectedSubject);
      params.append('sort', sortBy);
      
      const res = await fetch(`${BACKEND_URL}/api/forum/discussions?${params}`);
      const data = await res.json();
      
      if (data.success) {
        setDiscussions(data.discussions || []);
      }
    } catch (error) {
      console.error('Error fetching discussions:', error);
      // Use sample data if API fails
      setDiscussions(getSampleDiscussions());
    } finally {
      setLoading(false);
    }
  };

  const fetchTopicDetails = async (id) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/topic/${id}`);
      const data = await res.json();
      
      if (data.success) {
        setSelectedTopic(data.topic);
      }
    } catch (error) {
      console.error('Error fetching topic:', error);
      // Find from local discussions
      const topic = discussions.find(d => d.id === id);
      if (topic) setSelectedTopic(topic);
    }
  };

  const getSampleDiscussions = () => [
    {
      id: 'disc-1',
      title: 'How to approach Organic Chemistry reactions?',
      content: 'I am struggling with organic chemistry reaction mechanisms. Can someone share a good strategy to remember all the named reactions for JEE?',
      author: { name: 'Rahul S.', id: 'user-1' },
      subject: 'Chemistry',
      tags: ['organic', 'reactions', 'jee-main'],
      likes: 24,
      replies: 8,
      views: 156,
      isPinned: true,
      createdAt: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      replies_data: [
        {
          id: 'reply-1',
          content: 'I found that making flowcharts for each reaction class really helps! Start with the reagent and conditions, then write the product type.',
          author: { name: 'Priya M.', id: 'user-2' },
          likes: 12,
          createdAt: new Date(Date.now() - 1 * 60 * 60 * 1000).toISOString()
        }
      ]
    },
    {
      id: 'disc-2',
      title: 'Best books for Physics - Mechanics',
      content: 'Which books do you recommend for building a strong foundation in mechanics for JEE Advanced?',
      author: { name: 'Amit K.', id: 'user-3' },
      subject: 'Physics',
      tags: ['mechanics', 'books', 'jee-advanced'],
      likes: 45,
      replies: 15,
      views: 320,
      isPinned: false,
      createdAt: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      replies_data: []
    },
    {
      id: 'disc-3',
      title: 'Calculus doubt - Integration by parts',
      content: 'When should I use integration by parts vs substitution? I always get confused.',
      author: { name: 'Sneha R.', id: 'user-4' },
      subject: 'Mathematics',
      tags: ['calculus', 'integration', 'doubt'],
      likes: 18,
      replies: 6,
      views: 89,
      isPinned: false,
      createdAt: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toISOString(),
      replies_data: []
    },
    {
      id: 'disc-4',
      title: 'JEE Main 2024 - Paper Analysis',
      content: 'Let\'s discuss the difficulty level and expected cutoffs for the January session!',
      author: { name: 'Admin', id: 'admin' },
      subject: 'General',
      tags: ['jee-main', '2024', 'cutoff'],
      likes: 89,
      replies: 42,
      views: 1250,
      isPinned: true,
      createdAt: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      replies_data: []
    }
  ];

  const handleCreatePost = async () => {
    if (!isLoggedIn) {
      toast({
        title: "Login Required",
        description: "Please login to create a discussion post",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }

    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast({
        title: "Missing Information",
        description: "Please fill in the title and content",
        variant: "destructive"
      });
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/discussions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newPost,
          user_id: userId,
          user_name: userName,
          tags: newPost.tags.split(',').map(t => t.trim()).filter(t => t)
        })
      });

      const data = await res.json();
      
      if (data.success) {
        toast({
          title: "Discussion Created!",
          description: "Your post has been published"
        });
        setShowNewPostDialog(false);
        setNewPost({ title: '', content: '', subject: 'Physics', tags: '' });
        fetchDiscussions();
      }
    } catch (error) {
      // Add to local state for demo
      const newDiscussion = {
        id: `disc-${Date.now()}`,
        title: newPost.title,
        content: newPost.content,
        author: { name: userName, id: userId },
        subject: newPost.subject,
        tags: newPost.tags.split(',').map(t => t.trim()).filter(t => t),
        likes: 0,
        replies: 0,
        views: 1,
        isPinned: false,
        createdAt: new Date().toISOString(),
        replies_data: []
      };
      setDiscussions([newDiscussion, ...discussions]);
      setShowNewPostDialog(false);
      setNewPost({ title: '', content: '', subject: 'Physics', tags: '' });
      toast({
        title: "Discussion Created!",
        description: "Your post has been published"
      });
    }
  };

  const handleReply = async () => {
    if (!isLoggedIn) {
      toast({
        title: "Login Required",
        description: "Please login to reply",
        variant: "destructive"
      });
      return;
    }

    if (!replyContent.trim()) {
      toast({
        title: "Empty Reply",
        description: "Please write something",
        variant: "destructive"
      });
      return;
    }

    try {
      const res = await fetch(`${BACKEND_URL}/api/forum/topic/${selectedTopic.id}/reply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          content: replyContent,
          user_id: userId,
          user_name: userName
        })
      });

      const data = await res.json();
      
      if (data.success) {
        toast({ title: "Reply Posted!" });
        setReplyContent('');
        fetchTopicDetails(selectedTopic.id);
      }
    } catch (error) {
      // Add reply locally for demo
      const newReply = {
        id: `reply-${Date.now()}`,
        content: replyContent,
        author: { name: userName, id: userId },
        likes: 0,
        createdAt: new Date().toISOString()
      };
      setSelectedTopic({
        ...selectedTopic,
        replies: (selectedTopic.replies || 0) + 1,
        replies_data: [...(selectedTopic.replies_data || []), newReply]
      });
      setReplyContent('');
      toast({ title: "Reply Posted!" });
    }
  };

  const handleLike = async (topicId, replyId = null) => {
    if (!isLoggedIn) {
      toast({
        title: "Login Required",
        description: "Please login to like posts",
        variant: "destructive"
      });
      return;
    }

    // Update locally for instant feedback
    if (replyId && selectedTopic) {
      setSelectedTopic({
        ...selectedTopic,
        replies_data: selectedTopic.replies_data.map(r =>
          r.id === replyId ? { ...r, likes: r.likes + 1 } : r
        )
      });
    } else {
      setDiscussions(discussions.map(d =>
        d.id === topicId ? { ...d, likes: d.likes + 1 } : d
      ));
      if (selectedTopic?.id === topicId) {
        setSelectedTopic({ ...selectedTopic, likes: selectedTopic.likes + 1 });
      }
    }

    // API call
    try {
      await fetch(`${BACKEND_URL}/api/forum/like`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic_id: topicId, reply_id: replyId, user_id: userId })
      });
    } catch (error) {
      // Already updated locally
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const getSubjectColor = (subject) => {
    const colors = {
      'Physics': 'bg-blue-100 text-blue-700 border-blue-200',
      'Chemistry': 'bg-green-100 text-green-700 border-green-200',
      'Mathematics': 'bg-purple-100 text-purple-700 border-purple-200',
      'General': 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[subject] || colors['General'];
  };

  const filteredDiscussions = discussions.filter(d =>
    d.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    d.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Topic Detail View
  if (selectedTopic) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
        {/* Header */}
        <header className="bg-white border-b sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="sm" onClick={() => navigate('/forum')}>
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Forum
                </Button>
                <Badge className={getSubjectColor(selectedTopic.subject)}>
                  {selectedTopic.subject}
                </Badge>
                {selectedTopic.is_locked && (
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                    <Lock className="w-3 h-3 mr-1" /> Locked
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {isLoggedIn && (
                  <>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={handleSubscribe}
                      className={isSubscribed ? "bg-blue-50 text-blue-700 border-blue-200" : ""}
                    >
                      {isSubscribed ? (
                        <><BellOff className="w-4 h-4 mr-1" /> Subscribed</>
                      ) : (
                        <><Bell className="w-4 h-4 mr-1" /> Subscribe</>
                      )}
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openReportDialog('topic', selectedTopic.id)}
                      className="text-slate-500 hover:text-red-600"
                    >
                      <Flag className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        <main className="container mx-auto px-4 py-6 max-w-4xl">
          {/* Banned User Warning */}
          {isBanned && (
            <Card className="mb-6 bg-red-50 border-red-200">
              <CardContent className="p-4 flex items-center gap-3">
                <Shield className="w-6 h-6 text-red-500" />
                <div>
                  <p className="font-medium text-red-700">You are currently banned from the forum</p>
                  <p className="text-sm text-red-600">Reason: {banInfo?.reason || 'Violation of community guidelines'}</p>
                  {banInfo?.expires_at && (
                    <p className="text-sm text-red-600">Ban expires: {new Date(banInfo.expires_at).toLocaleDateString()}</p>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Original Post */}
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {selectedTopic.isPinned && (
                    <Badge variant="outline" className="mb-2 bg-amber-50 text-amber-700 border-amber-200">
                      <Pin className="w-3 h-3 mr-1" /> Pinned
                    </Badge>
                  )}
                  <CardTitle className="text-xl">{selectedTopic.title}</CardTitle>
                  <div className="flex items-center gap-3 mt-2 text-sm text-slate-500">
                    <div className="flex items-center gap-1">
                      <Avatar className="w-6 h-6">
                        <AvatarFallback className="text-xs bg-blue-100 text-blue-700">
                          {selectedTopic.author?.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <span>{selectedTopic.author?.name}</span>
                    </div>
                    <span>•</span>
                    <span>{formatTimeAgo(selectedTopic.createdAt)}</span>
                    <span>•</span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" /> {selectedTopic.views} views
                    </span>
                  </div>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 whitespace-pre-wrap">{selectedTopic.content}</p>
              
              {selectedTopic.tags?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-4">
                  {selectedTopic.tags.map((tag, i) => (
                    <Badge key={i} variant="secondary" className="text-xs">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
            <CardFooter className="border-t pt-4">
              <div className="flex items-center gap-4">
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="gap-2"
                  onClick={() => handleLike(selectedTopic.id)}
                >
                  <Heart className="w-4 h-4" />
                  {selectedTopic.likes}
                </Button>
                <span className="text-sm text-slate-500 flex items-center gap-1">
                  <MessageCircle className="w-4 h-4" />
                  {selectedTopic.replies} replies
                </span>
              </div>
            </CardFooter>
          </Card>

          {/* Replies */}
          <div className="space-y-4 mb-6">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Replies ({selectedTopic.replies_data?.length || 0})
            </h3>
            
            {selectedTopic.replies_data?.length > 0 ? (
              selectedTopic.replies_data.map((reply) => (
                <Card key={reply.id} className="border-l-4 border-l-blue-200">
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <Avatar className="w-8 h-8">
                        <AvatarFallback className="text-sm bg-slate-100">
                          {reply.author?.name?.charAt(0) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{reply.author?.name}</span>
                          <span className="text-xs text-slate-400">{formatTimeAgo(reply.createdAt)}</span>
                        </div>
                        <p className="text-slate-700 text-sm">{reply.content}</p>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="mt-2 h-7 text-xs gap-1"
                          onClick={() => handleLike(selectedTopic.id, reply.id)}
                        >
                          <ThumbsUp className="w-3 h-3" />
                          {reply.likes}
                        </Button>
                        {isLoggedIn && (
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="mt-2 h-7 text-xs text-slate-400 hover:text-red-500"
                            onClick={(e) => {
                              e.stopPropagation();
                              openReportDialog('reply', reply.id);
                            }}
                          >
                            <Flag className="w-3 h-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            ) : (
              <Card className="bg-slate-50 border-dashed">
                <CardContent className="py-8 text-center text-slate-500">
                  <MessageSquare className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p>No replies yet. Be the first to respond!</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Reply Form */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Write a Reply</CardTitle>
            </CardHeader>
            <CardContent>
              {selectedTopic.is_locked ? (
                <div className="text-center py-4">
                  <Lock className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-slate-600">This topic is locked and no longer accepts replies</p>
                </div>
              ) : isBanned ? (
                <div className="text-center py-4">
                  <Shield className="w-10 h-10 mx-auto mb-2 text-red-400" />
                  <p className="text-red-600 mb-1">You are banned from posting</p>
                  <p className="text-sm text-slate-500">Contact support if you believe this is a mistake</p>
                </div>
              ) : isLoggedIn ? (
                <div className="space-y-4">
                  <Textarea
                    placeholder="Share your thoughts, tips, or solutions..."
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    rows={4}
                  />
                  <Button onClick={handleReply} className="gap-2">
                    <Send className="w-4 h-4" />
                    Post Reply
                  </Button>
                </div>
              ) : (
                <div className="text-center py-4">
                  <AlertCircle className="w-10 h-10 mx-auto mb-2 text-amber-500" />
                  <p className="text-slate-600 mb-3">Please login to reply</p>
                  <Button onClick={() => navigate('/login')}>Login to Reply</Button>
                </div>
              )}
            </CardContent>
          </Card>
        </main>

        {/* Report Dialog */}
        <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Flag className="w-5 h-5 text-red-500" />
                Report Content
              </DialogTitle>
              <DialogDescription>
                Help us maintain a safe community by reporting inappropriate content
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Reason for reporting</label>
                <Select value={reportReason} onValueChange={setReportReason}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="spam">Spam or advertising</SelectItem>
                    <SelectItem value="harassment">Harassment or bullying</SelectItem>
                    <SelectItem value="inappropriate">Inappropriate content</SelectItem>
                    <SelectItem value="misinformation">Misinformation</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {reportReason === 'other' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">Additional details</label>
                  <Textarea
                    placeholder="Please describe the issue..."
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                    rows={3}
                  />
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowReportDialog(false)}>Cancel</Button>
              <Button variant="destructive" onClick={handleReport}>Submit Report</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // Forum List View
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="sm" onClick={() => navigate('/test-series')}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Test Series
              </Button>
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-slate-900">Discussion Forum</h1>
                <p className="text-sm text-slate-500">Ask doubts, share tips, help others</p>
              </div>
            </div>
            <Button onClick={() => setShowNewPostDialog(true)} className="gap-2">
              <Plus className="w-4 h-4" />
              <span className="hidden sm:inline">New Discussion</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Stats Banner */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
            <CardContent className="p-4 text-center">
              <MessageSquare className="w-6 h-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{discussions.length}</p>
              <p className="text-xs opacity-80">Discussions</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0">
            <CardContent className="p-4 text-center">
              <User className="w-6 h-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">500+</p>
              <p className="text-xs opacity-80">Members</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0">
            <CardContent className="p-4 text-center">
              <MessageCircle className="w-6 h-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">{discussions.reduce((acc, d) => acc + d.replies, 0)}</p>
              <p className="text-xs opacity-80">Replies</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0">
            <CardContent className="p-4 text-center">
              <Award className="w-6 h-6 mx-auto mb-1 opacity-80" />
              <p className="text-2xl font-bold">50+</p>
              <p className="text-xs opacity-80">Experts</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Search discussions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Subject" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Subjects</SelectItem>
                  <SelectItem value="Physics">Physics</SelectItem>
                  <SelectItem value="Chemistry">Chemistry</SelectItem>
                  <SelectItem value="Mathematics">Mathematics</SelectItem>
                  <SelectItem value="General">General</SelectItem>
                </SelectContent>
              </Select>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="recent">Most Recent</SelectItem>
                  <SelectItem value="popular">Most Popular</SelectItem>
                  <SelectItem value="replies">Most Replies</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Discussions List */}
        <div className="space-y-4">
          {loading ? (
            <Card className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-slate-200 rounded w-3/4 mb-4"></div>
                <div className="h-3 bg-slate-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ) : filteredDiscussions.length > 0 ? (
            filteredDiscussions.map((discussion) => (
              <Card 
                key={discussion.id} 
                className={`hover:shadow-md transition-shadow cursor-pointer ${discussion.isPinned ? 'border-amber-200 bg-amber-50/30' : ''}`}
                onClick={() => navigate(`/forum/${discussion.id}`)}
                data-testid={`discussion-${discussion.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    <Avatar className="w-10 h-10 hidden sm:flex">
                      <AvatarFallback className="bg-blue-100 text-blue-700">
                        {discussion.author?.name?.charAt(0) || 'U'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        {discussion.isPinned && (
                          <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-200 text-xs">
                            <Pin className="w-3 h-3 mr-1" /> Pinned
                          </Badge>
                        )}
                        <Badge className={`${getSubjectColor(discussion.subject)} text-xs`}>
                          {discussion.subject}
                        </Badge>
                      </div>
                      <h3 className="font-semibold text-slate-900 truncate">{discussion.title}</h3>
                      <p className="text-sm text-slate-500 line-clamp-2 mt-1">{discussion.content}</p>
                      
                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-400">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" /> {discussion.author?.name}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {formatTimeAgo(discussion.createdAt)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart className="w-3 h-3" /> {discussion.likes}
                        </span>
                        <span className="flex items-center gap-1">
                          <MessageCircle className="w-3 h-3" /> {discussion.replies}
                        </span>
                        <span className="flex items-center gap-1">
                          <Eye className="w-3 h-3" /> {discussion.views}
                        </span>
                      </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="bg-slate-50 border-dashed">
              <CardContent className="py-12 text-center">
                <MessageSquare className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <h3 className="font-medium text-slate-700 mb-1">No discussions found</h3>
                <p className="text-sm text-slate-500 mb-4">Be the first to start a discussion!</p>
                <Button onClick={() => setShowNewPostDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" /> Create Discussion
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>

      {/* New Discussion Dialog */}
      <Dialog open={showNewPostDialog} onOpenChange={setShowNewPostDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Create New Discussion</DialogTitle>
            <DialogDescription>
              Ask a doubt, share tips, or start a discussion with the community
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title</label>
              <Input
                placeholder="What's your question or topic?"
                value={newPost.title}
                onChange={(e) => setNewPost({ ...newPost, title: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Subject</label>
              <Select value={newPost.subject} onValueChange={(v) => setNewPost({ ...newPost, subject: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Physics">Physics</SelectItem>
                  <SelectItem value="Chemistry">Chemistry</SelectItem>
                  <SelectItem value="Mathematics">Mathematics</SelectItem>
                  <SelectItem value="General">General</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Content</label>
              <Textarea
                placeholder="Describe your question or share your thoughts in detail..."
                value={newPost.content}
                onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                rows={5}
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Tags (comma separated)</label>
              <Input
                placeholder="e.g., mechanics, jee-main, doubt"
                value={newPost.tags}
                onChange={(e) => setNewPost({ ...newPost, tags: e.target.value })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewPostDialog(false)}>Cancel</Button>
            <Button onClick={handleCreatePost}>Create Discussion</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DiscussionForum;
